import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Link,
    Stack,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Email as EmailIcon,
    MailOutline as MailOutlineIcon,
    AttachFile as AttachFileIcon,
    Inbox as InboxIcon,
} from '@mui/icons-material';
import { useGetMailsQuery } from '@/store/apiSlice';
import { themeConstants } from '@/theme';
import BaseModal from '@/components/modals/BaseModal';
import { close } from '@/store/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { useI18nContext } from '@/i18n/i18n-react';

const MailboxModal = () => {
    const dispatch = useAppDispatch();
    const open = useAppSelector((state) => state.ui.open.mailbox);
    const onClose = () => dispatch(close('mailbox'));
    const { LL } = useI18nContext();

    const { data: mails = [], isLoading: loading } = useGetMailsQuery(6, { skip: !open });

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date
            .toLocaleDateString(undefined, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
            })
            .replace(/,/g, '');
    };

    return (
        <BaseModal
            open={open}
            onClose={onClose}
            maxWidth="lg"
            styledHeader={{
                icon: <InboxIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: 'white' }} />,
                title: LL.mailboxModal.title(),
                subtitle: [LL.mailboxModal.subtitle?.() || LL.mailboxModal.recentMessages()],
                gradient: themeConstants.gradients.primary,
            }}
            loading={loading}
        >
            <Box>
                {mails.map((mail) => (
                    <Accordion key={mail.messageNo}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                backgroundColor: mail.unseen ? 'action.hover' : 'transparent',
                                fontWeight: mail.unseen ? 'bold' : 'normal',
                            }}
                        >
                            <Stack
                                direction="row"
                                sx={{
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    width: '100%',
                                    gap: 1,
                                }}
                            >
                                {mail.unseen ? <EmailIcon /> : <MailOutlineIcon />}
                                <Typography variant="body2" color="text.secondary">
                                    {formatDate(mail.date)}
                                </Typography>
                                <Typography sx={{ fontWeight: 'inherit' }}>
                                    <strong>{mail.sender}</strong>
                                </Typography>
                                {mail.attachments && mail.attachments.length > 0 && (
                                    <AttachFileIcon />
                                )}
                                <Box sx={{ flex: 1 }} />
                                <Typography variant="body2">{mail.subject}</Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <Typography
                                    component="div"
                                    dangerouslySetInnerHTML={{
                                        __html: mail.body.replace(/\n/g, '<br />'),
                                    }}
                                    sx={{ mb: 2, whiteSpace: 'pre-line', wordBreak: 'break-word' }}
                                />
                                {mail.attachments?.length && mail.attachments.length > 0 && (
                                    <>
                                        <Typography variant="h6" gutterBottom>
                                            Attachments
                                        </Typography>
                                        <List>
                                            {mail.attachments.map((attachment, idx) => (
                                                <ListItem key={idx}>
                                                    <ListItemIcon>
                                                        <AttachFileIcon />
                                                    </ListItemIcon>
                                                    <ListItemText>
                                                        <Link
                                                            href={`/rest/Attachment/${mail.messageNo}/${attachment.section}/${encodeURI(attachment.filename)}`}
                                                            target="_blank"
                                                        >
                                                            {attachment.filename}
                                                        </Link>
                                                    </ListItemText>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </>
                                )}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>
        </BaseModal>
    );
};

export default MailboxModal;
